import { Component, Input, ElementRef, HostListener, inject } from '@angular/core';
import { CardVariant } from './featureCardInterface';

const ICONS: Record<number, string> = {
  0: 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
  1: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z',
  2: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  3: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
};

@Component({
  selector: 'app-feature-card',
  standalone: true,
  imports: [],
  template: `
    <article
      class="glow-card rounded-2xl h-full flex flex-col gap-4 p-6"
      [style]="cardStyle"
      style="transition:transform 0.4s cubic-bezier(0.16,1,0.3,1),box-shadow 0.4s ease"
    >
      <!-- Shimmer stripe -->
      <div class="shimmer-stripe"></div>

      <!-- Icon -->
      <div
        class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style="background:rgba(0,140,240,0.12);border:1px solid rgba(0,140,240,0.28);transition:background 0.3s,border-color 0.3s"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" style="color:#008CF0"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
          <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="iconPath"/>
        </svg>
      </div>

      <!-- Aurrica icon as subtle bg accent -->
      <img
        src="/assets/images/icon.png"
        alt="" aria-hidden="true"
        class="absolute opacity-[0.04] pointer-events-none"
        style="width:70px;bottom:8px;right:8px;mix-blend-mode:screen;filter:drop-shadow(0 0 8px rgba(0,140,240,0.5))"
        loading="lazy"
      />

      <!-- Text -->
      <div class="relative flex flex-col flex-1" style="z-index:5">
        <h3 class="font-display font-bold text-base mb-2 leading-snug" style="color:var(--theme-text)">{{ title }}</h3>
        <p class="text-sm leading-relaxed" style="color:var(--theme-text-dim)">{{ description }}</p>
      </div>
    </article>
  `,
})
export class FeatureCardComponent {
  @Input() title = '';
  @Input() description = '';
  @Input() variant: CardVariant = 'elevated';
  @Input() index = 0;

  private el = inject(ElementRef);

  get iconPath(): string { return ICONS[this.index % 4]; }

  get cardStyle(): string {
    return 'background:var(--glass-bg);border:1px solid var(--glass-border);position:relative;overflow:hidden';
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    const el = this.el.nativeElement as HTMLElement;
    const rect = el.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top)  / rect.height;

    el.style.setProperty('--mouse-x', `${nx * 100}%`);
    el.style.setProperty('--mouse-y', `${ny * 100}%`);

    const tiltX = (ny - 0.5) * -8;
    const tiltY = (nx - 0.5) *  8;
    el.style.transform = `perspective(800px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-4px) scale(1.012)`;

    /* brighten icon badge on hover */
    const icon = el.querySelector('div[style*="background:rgba(0,140"]') as HTMLElement | null;
    if (icon) {
      icon.style.background    = 'rgba(0,140,240,0.28)';
      icon.style.borderColor   = 'rgba(0,212,255,0.5)';
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    const el = this.el.nativeElement as HTMLElement;
    el.style.transform = '';
    el.style.removeProperty('--mouse-x');
    el.style.removeProperty('--mouse-y');
    const icon = el.querySelector('div[style*="background:rgba(0,140"]') as HTMLElement | null;
    if (icon) {
      icon.style.background  = '';
      icon.style.borderColor = '';
    }
  }
}
